<script setup lang="ts">
/**
 * SectionHeader Component
 * 
 * A unified header for sections, featuring a massive animated title,
 * a signature separator bar, and an optional subtitle.
 */

interface Props {
  // Title Parts
  titlePrefix?: string
  titleHighlight?: string
  titleSuffix?: string
  
  // Alternative: Single title string
  title?: string
  
  subtitle?: string
  
  align?: 'center' | 'left'
  
  // Visuals
  highlightMode?: 'gradient' | 'solid'
  highlightColor?: 'red' | 'yellow'
  showSeparator?: boolean
  size?: 'normal' | 'small'
}

const props = withDefaults(defineProps<Props>(), {
  titlePrefix: '',
  titleHighlight: '',
  titleSuffix: '',
  title: '',
  subtitle: '',
  align: 'center',
  highlightMode: 'gradient',
  highlightColor: 'red',
  showSeparator: true,
  size: 'normal'
})

const highlightClasses = computed(() => {
  if (props.highlightMode === 'solid') {
    return props.highlightColor === 'yellow' ? 'text-oxo-yellow' : 'text-oxo-red'
  }
  return 'text-transparent bg-clip-text bg-linear-to-r from-oxo-red to-oxo-yellow'
})
</script>

<template>
  <div 
    :class="[
      'mb-16 animate-fade-in-up',
      align === 'center' ? 'text-center' : 'text-left'
    ]"
  >
    <!-- Title -->
    <h2 
      :class="[
        'font-display font-black uppercase leading-[0.85] flex flex-wrap items-baseline gap-y-2 py-4',
        size === 'small' 
          ? (align === 'center' ? 'text-2xl md:text-3xl justify-center' : 'text-xl md:text-2xl justify-start')
          : (align === 'center' ? 'text-4xl md:text-6xl lg:text-7xl justify-center' : 'text-3xl md:text-5xl lg:text-6xl justify-start')
      ]"
    >
      <span v-if="titlePrefix" class="text-foreground mr-[0.2em]">{{ titlePrefix }}</span>
      
      <span 
        v-if="titleHighlight" 
        :class="highlightClasses"
        class="inline-block px-[0.05em] mx-[-0.05em]"
      >
        {{ titleHighlight }}
      </span>

      <span v-if="titleSuffix" class="text-foreground ml-[0.2em]">{{ titleSuffix }}</span>
      <span v-else-if="title && !titleHighlight" class="text-foreground">{{ title }}</span>
    </h2>

    <!-- Signature Separator Bar -->
    <div v-if="showSeparator" 
      class="h-1.5 bg-linear-to-r from-oxo-yellow to-oxo-red rounded-full animate-width-grow mt-6"
      :class="[
        align === 'center' ? 'mx-auto' : 'w-32',
        subtitle ? 'mb-8' : 'mb-0'
      ]"
    ></div>

    <!-- Subtitle -->
    <p v-if="subtitle" 
      class="text-lg md:text-xl text-foreground-dim max-w-2xl font-light leading-relaxed animate-fade-in-up transition-all duration-300"
      :class="[
        align === 'center' ? 'mx-auto text-balance' : '',
        showSeparator ? '' : 'mt-6'
      ]"
      style="animation-delay: 0.2s"
      v-html="subtitle"
    ></p>
  </div>
</template>
